import React, { useEffect, useState } from 'react';
import { X, Phone, Mail, MessageSquare, Users, MapPin, FileText, Send, Loader2, Calendar } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import ActivityTimeline from './ActivityTimeline';
import { DISPOSITIONS_BY_TYPE, NO_DETAIL_DISPOSITIONS } from '../../lib/activityDispositions';

const ACTIVITY_TYPES = [
  { key: 'call',       icon: Phone,         label: 'Call',          color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'meeting',    icon: Users,         label: 'Meeting',       color: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'email',      icon: Mail,          label: 'Email',         color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'whatsapp',   icon: MessageSquare, label: 'WhatsApp',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'site_visit', icon: MapPin,        label: 'Site Visit',    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'note',       icon: FileText,      label: 'Note',          color: 'bg-slate-50 text-slate-700 border-slate-200' },
];

/**
 * Drawer for displaying & logging activities against a Company or Contact.
 *
 * Props:
 *   entityType: 'company' | 'contact'
 *   entityId: id of the record
 *   entityLabel: human label for the title bar
 *   subtitle: optional secondary line under title
 *   onClose: () => void
 *   metaRows: optional array of {label, value} rendered in the info panel
 */
export default function EntityActivityDrawer({ entityType, entityId, entityLabel, subtitle, onClose, metaRows = [], bottomSlot = null }) {
  const [activeType, setActiveType] = useState('call');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [duration, setDuration] = useState('');
  const [disposition, setDisposition] = useState('');
  const needsDetail = !NO_DETAIL_DISPOSITIONS.has(disposition);
  const [busy, setBusy] = useState(false);
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Follow-up state
  const [fuOpen, setFuOpen] = useState(false);
  const [fuDate, setFuDate] = useState('');
  const [fuTime, setFuTime] = useState('09:00');
  const [fuActivity, setFuActivity] = useState('call');
  const [fuDescription, setFuDescription] = useState('');
  const [fuBusy, setFuBusy] = useState(false);

  const apiBase = entityType === 'company' ? '/crm/companies' : '/crm/contacts';

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet(`${apiBase}/${entityId}/activity`);
      setComms(r.communications || []);
    } catch (err) { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [entityType, entityId]); // eslint-disable-line

  // Reset disposition when activity type changes
  useEffect(() => { setDisposition(''); }, [activeType]);

  // Clear stale subject/notes when switching to a no-detail disposition
  useEffect(() => {
    if (!needsDetail) { setSubject(''); setNotes(''); }
  }, [disposition]); // eslint-disable-line

  const log = async () => {
    if (needsDetail && !subject.trim()) { toast.error('Subject is required'); return; }
    setBusy(true);
    try {
      await apiPost(`${apiBase}/${entityId}/communications`, {
        type: activeType,
        subject: subject.trim() || null,
        notes: notes.trim() || null,
        duration_minutes: duration ? parseInt(duration) : null,
        disposition: disposition || null,
      });
      const savedDisposition = disposition;
      const savedType = activeType;
      setSubject(''); setNotes(''); setDuration(''); setDisposition('');
      toast.success('Activity logged');
      load();
      // Auto-open follow-up form when disposition is "Requested Follow-up"
      if (savedDisposition && savedDisposition.includes('Requested Follow-up')) {
        setFuOpen(true);
        setFuActivity(savedType === 'note' ? 'other' : savedType);
        setFuDescription(`Follow-up from ${savedType} on ${new Date().toLocaleDateString('en-IN')}`);
        toast.info('Please schedule the follow-up.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  const scheduleFollowUp = async () => {
    if (!fuDate) { toast.error('Pick a date'); return; }
    if (!fuDescription.trim()) { toast.error('Add a description'); return; }
    setFuBusy(true);
    try {
      await apiPost('/crm/reminders', {
        entity_type:          entityType,
        entity_id:            entityId,
        reminder_date:        `${fuDate}T${fuTime}:00`,
        description:          `[${fuActivity.toUpperCase()}] ${fuDescription}`,
        linked_entity_type:   entityType,
        linked_entity_id:     entityId,
        linked_entity_label:  entityLabel,
        priority:             'Normal',
      });
      toast.success(`Follow-up scheduled for ${fuDate} at ${fuTime}`);
      setFuOpen(false);
      setFuDate(''); setFuTime('09:00');
      setFuActivity('call'); setFuDescription('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not schedule follow-up');
    }
    setFuBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid={`${entityType}-detail-drawer`}>
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-[700px] bg-slate-50 border-l border-slate-200 flex flex-col">
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">{entityType}</div>
            <h2 className="font-display text-xl font-bold mt-1 truncate" title={entityLabel}>{entityLabel}</h2>
            {subtitle && <div className="text-xs text-slate-600 mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} data-testid="close-drawer"><X size={18} /></button>
        </div>

        {/* Meta */}
        {metaRows.length > 0 && (
          <div className="bg-white border-b border-slate-200 px-6 py-3 grid grid-cols-2 gap-x-6 gap-y-1.5">
            {metaRows.map((m, idx) => (
              <div key={idx} className="text-xs">
                <span className="text-slate-500 uppercase tracking-wider text-[10px] font-display font-bold">{m.label}</span>
                <div className="text-slate-800 mt-0.5 truncate">{m.value || '—'}</div>
              </div>
            ))}
          </div>
        )}

        {/* Quick-log */}
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Log Activity</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {ACTIVITY_TYPES.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveType(t.key)}
                  className={`inline-flex items-center gap-1 text-xs uppercase tracking-wider font-display font-bold px-2.5 py-1.5 border rounded-sm ${activeType === t.key ? t.color : 'bg-white border-slate-200 text-slate-500 hover:border-[#3b4872]'}`}
                  data-testid={`activity-type-${t.key}`}
                >
                  <Icon size={12} /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Disposition dropdown */}
          <div className="mb-3">
            <label className="block text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-1">
              Outcome / Disposition
            </label>
            <select
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:border-[#3b4872]"
              data-testid="activity-disposition"
            >
              <option value="">— Select outcome —</option>
              {(DISPOSITIONS_BY_TYPE[activeType] || []).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {needsDetail && (
            <>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Subject (e.g. Discussed BOQ revision)"
                className="w-full mb-2 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#3b4872]"
                data-testid="activity-subject"
              />
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Notes / outcome (optional)"
                rows={2}
                className="w-full mb-2 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#3b4872]"
                data-testid="activity-notes"
              />
            </>
          )}
          <div className="flex gap-2 items-end">
            {(activeType === 'call' || activeType === 'meeting') && (
              <div className="flex-shrink-0">
                <input
                  type="number"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  placeholder="Duration (min)"
                  className="w-32 px-3 py-2 border border-slate-200 rounded-sm text-sm"
                  data-testid="activity-duration"
                />
              </div>
            )}
            <button
              onClick={log}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-[#3b4872] hover:bg-[#2c365a] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
              data-testid="log-activity-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Log {ACTIVITY_TYPES.find(t => t.key === activeType)?.label}
            </button>
          </div>
        </div>

        {/* Follow-up Scheduler */}
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">
              Schedule Follow-up
            </div>
            {!fuOpen && (
              <button
                onClick={() => setFuOpen(true)}
                className="text-xs text-[#3b4872] font-semibold hover:underline flex items-center gap-1"
                data-testid="schedule-followup-btn"
              >
                <Calendar size={12} /> Schedule
              </button>
            )}
          </div>

          {fuOpen && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold block mb-1">Date *</label>
                  <input type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs"
                    data-testid="followup-date" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold block mb-1">Time</label>
                  <input type="time" value={fuTime} onChange={(e) => setFuTime(e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs"
                    data-testid="followup-time" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold block mb-1">Activity</label>
                  <select value={fuActivity} onChange={(e) => setFuActivity(e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs bg-white"
                    data-testid="followup-activity">
                    <option value="call">Call</option>
                    <option value="meeting">Meeting</option>
                    <option value="email">Email</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="site_visit">Site Visit</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <textarea value={fuDescription} onChange={(e) => setFuDescription(e.target.value)}
                placeholder="What is this follow-up about?"
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#3b4872]"
                data-testid="followup-description" />
              <div className="flex justify-end gap-2">
                <button onClick={() => { setFuOpen(false); setFuDate(''); setFuDescription(''); }}
                  className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700">
                  Cancel
                </button>
                <button onClick={scheduleFollowUp} disabled={fuBusy}
                  className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#3b4872] hover:bg-[#2c365a] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
                  data-testid="save-followup-btn">
                  {fuBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Schedule
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Activity Timeline ({comms.length})</div>
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : comms.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8">No activity logged yet. Log your first interaction above.</div>
          ) : (
            <ActivityTimeline communications={comms} />
          )}
          {bottomSlot && <div className="mt-6">{bottomSlot}</div>}
        </div>
      </div>
    </div>
  );
}
